import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { UserContext } from '../../../../App'
import {
  SectionHeader,
  InfoList,
} from '../../vehicles/VehicleComponents/SectionKit'
import {
  canManageMakeReady,
  useMakeReadyTierAllowed,
} from '../../makeReadyModule/makeReadyAccess'
import { AssetContext } from './SingleAsset'
import useAssetActions from './useAssetActions'

// A value with its action beside it, for the Location & Custody rows.
const ValueWithAction = ({ value, muted, action }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
    <Typography
      variant="body2"
      fontWeight={600}
      color={muted ? 'text.disabled' : 'text.primary'}
      textAlign="right"
    >
      {value}
    </Typography>
    {action}
  </Stack>
)

// Landing tab: the asset's own record, and where it is / who has it.
const AssetOverview = () => {
  const { asset, openDialog } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const navigate = useNavigate()
  const {
    canCheckIn,
    checkedOutToMe,
    checkIn,
    checkOutToMe,
    unassignVehicle,
    removeFromParent,
    removeFromStore,
  } = useAssetActions()
  const canAssignLoadList =
    useMakeReadyTierAllowed(2) && canManageMakeReady(user)

  const holderName = asset.checkedOutTo
    ? `${asset.checkedOutTo.firstName ?? ''} ${asset.checkedOutTo.lastName ?? ''}`.trim()
    : ''

  const detailRows = [
    { label: 'Asset ID', value: asset.assetID },
    { label: 'Category', value: asset.category },
    { label: 'Sub Category', value: asset.subCategory },
    { label: 'Manufacturer', value: asset.manufacturer },
    { label: 'Brand', value: asset.brand },
    { label: 'Model', value: asset.model },
    { label: 'Serial Number', value: asset.serialNumber },
    {
      label: 'Requires Servicing',
      value: asset.requiresServicing ? 'Yes' : 'No',
    },
    {
      label: 'Make Ready Load List',
      value: asset.makeReadyLoadList?.title ? (
        asset.makeReadyLoadList.title
      ) : canAssignLoadList ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Not set — assign one"
          onClick={() => openDialog('assignLoadList')}
          sx={{ borderRadius: 1, fontWeight: 700 }}
        />
      ) : (
        'Not set'
      ),
    },
  ]

  const locationRows = [
    {
      label: 'Location',
      value: asset.vehicle ? (
        <ValueWithAction
          value={
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => navigate('/fleet/' + asset.vehicle._id)}
              sx={{ font: 'inherit', fontWeight: 600 }}
            >
              {asset.vehicle.callSign}
            </Link>
          }
          action={
            <Button size="small" onClick={unassignVehicle}>
              Unassign
            </Button>
          }
        />
      ) : asset.parentAsset?.assetID ? (
        <ValueWithAction
          value={
            <>
              Inside{' '}
              <Link
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                onClick={() =>
                  navigate('/asset-manager/' + asset.parentAsset.assetID)
                }
                sx={{ font: 'inherit', fontWeight: 600 }}
              >
                {asset.parentAsset.assetID}
              </Link>
              {asset.parentAsset.vehicle?.callSign
                ? ` (on ${asset.parentAsset.vehicle.callSign})`
                : ''}
            </>
          }
          action={
            <Button size="small" onClick={removeFromParent}>
              Remove
            </Button>
          }
        />
      ) : asset.store?.name ? (
        <ValueWithAction
          value={
            <>
              In {asset.store.name}
              {asset.store.baseLocation?.name
                ? ` (${asset.store.baseLocation.name})`
                : ''}
            </>
          }
          action={
            <Button size="small" onClick={removeFromStore}>
              Remove
            </Button>
          }
        />
      ) : (
        <ValueWithAction
          value="Not on a vehicle"
          muted
          action={
            <Button size="small" onClick={() => openDialog('assignVehicle')}>
              Assign to vehicle
            </Button>
          }
        />
      ),
    },
    {
      label: 'Checked out to',
      value: asset.checkedOutTo ? (
        <ValueWithAction
          value={checkedOutToMe ? `${holderName} (you)` : holderName}
          action={
            canCheckIn ? (
              <Button size="small" onClick={checkIn}>
                Check in
              </Button>
            ) : null
          }
        />
      ) : (
        <ValueWithAction
          value="Nobody"
          muted
          action={
            <Button size="small" onClick={checkOutToMe}>
              Check out to me
            </Button>
          }
        />
      ),
    },
  ]

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <SectionHeader
            title="Asset Details"
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => openDialog('edit')}
              >
                Edit
              </Button>
            }
          />
          <InfoList rows={detailRows} />
          <Divider sx={{ my: 1.5 }} />
          <SectionHeader title="Notes" />
          <Typography
            variant="body2"
            color={asset.notes ? 'text.primary' : 'text.disabled'}
            sx={{ whiteSpace: 'pre-line', mt: 1 }}
          >
            {asset.notes || 'No notes recorded on this asset.'}
          </Typography>
        </Card>
      </Grid>
      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <SectionHeader
            title="Location & Custody"
            caption="Where the asset lives and who has it right now"
          />
          <InfoList rows={locationRows} />
        </Card>
      </Grid>
    </Grid>
  )
}

export default AssetOverview
